import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react'
import { observable, runInAction } from 'mobx'
import { APPConfig, convertToBoolean } from 'config/appConfig'
import { showAllPromoCodesInCheckout } from 'config/appConfig/configUtils'
import {
  i18nTranslate,
  isExpressCheckout,
  trackCheckoutCouponCodeEvents,
  trackRedesignAddCoupon,
  trackRedesignViewPromotion,
  trackRedesignSelectPromotion,
  enableNewFeature,
  isFeatureEnabled,
} from 'src/utils'
import {
  checkoutContainer,
  cartContainer,
  loyaltyContainer,
  subscriptionContainer,
  customerContainer,
} from 'src/models'
import {
  getLocalStorage,
  setLocalStorage,
  deleteFromLocalStorage,
} from 'src/utils'
import { CommonForm, toastState } from 'src/views/components'
import { dateFormatDeps } from 'src/deps'
import {
  PromoStackingService,
  getScopedAppliedPromoKey,
  migrateOldPromoStorageKey,
} from 'src/utils/promoStackingService'
import { formInput, OFFER_TYPES } from './formInput'
import {
  IoIosClose as CloseIcon,
  IoIosArrowDown as DownwardArrowIcon,
  IoIosArrowUp as UpwardArrowIcon,
} from 'react-icons/io'
import { schema } from './validationSchema'
import './styles.scss'
import { FaExclamationTriangle } from 'react-icons/fa'

export let globalTrackingState = {
  hasTrackedInitialPromotions: false,
  trackedPromotionIds: new Set(),
  reset: function () {
    globalTrackingState.hasTrackedInitialPromotions = false
    globalTrackingState.trackedPromotionIds.clear()
  },
}

@observer
class PromoWalletCheckout extends React.Component {
  @observable showMore = false
  showMoreDefaultLength = 2
  @observable promoList = []
  formikRef = React.createRef()
  @observable isCallInProgress = false
  @observable isToResetForm = false
  @observable disablePromoStacking =
    convertToBoolean(APPConfig?.getAppConfig()?.disablePromoStacking) || false
  @observable promoCodeRegexToAllowStacking =
    APPConfig?.getAppConfig()?.promoCodeRegexToAllowStacking || ''
  @observable toShowAlertMsg = false
  @observable previousAppliedPromo = ''
  @observable currentAppliedPromo = ''
  @observable isReplacingPromo = false
  @observable appliedPromoPreview = null
  @observable hasRunPromoCleanup = false
  @observable voucherAppliedMap = {}
  @observable isVoucherConfirmModalOpen = false
  @observable pendingVoucher = null
  @observable isApplyVoucherDisabled = false

  isFromSubscription = this.props?.isFromSubscription || false

  get promoStackingService() {
    return new PromoStackingService({
      disablePromoStacking: this.disablePromoStacking,
      promoCodeRegexToAllowStacking: this.promoCodeRegexToAllowStacking,
    })
  }

  getPromoListRequestHeaders = () => {
    return this.isFromSubscription ? { 'x-summary-cart': true } : {}
  }

  getFetchAllPromoCodesParam = () => {
    return showAllPromoCodesInCheckout() ? 'true' : 'false'
  }

  initializeLoyaltyData = async isLoyaltyVoucherEnabled => {
    const isLoggedIn = customerContainer?.isRegisterUser
    if (isLoggedIn && isLoyaltyVoucherEnabled) {
      await loyaltyContainer?.getLoyalty?.()
    }

    if (!isLoyaltyVoucherEnabled) {
      return
    }

    const alreadyApplied = cartContainer?.cartResponse?.loyaltyDetails || []
    alreadyApplied
      .filter(item => item?.type === 'VOUCHER')
      .forEach(item => {
        this.voucherAppliedMap[item.voucherType] = true
      })
  }

  clearStoredPromoValues = promoStorageKey => {
    if (promoStorageKey) {
      deleteFromLocalStorage(promoStorageKey)
      deleteFromLocalStorage(`${promoStorageKey}_previous`)
      deleteFromLocalStorage(`${promoStorageKey}_current`)
      return
    }

    deleteFromLocalStorage('appliedPromoCode')
    deleteFromLocalStorage('appliedPromoPrevious')
    deleteFromLocalStorage('appliedPromoCurrent')
  }

  clearStoredPromoReplacementValues = promoStorageKey => {
    if (promoStorageKey) {
      deleteFromLocalStorage(`${promoStorageKey}_previous`)
      deleteFromLocalStorage(`${promoStorageKey}_current`)
      return
    }

    deleteFromLocalStorage('appliedPromoPrevious')
    deleteFromLocalStorage('appliedPromoCurrent')
  }

  readStoredPromoValues = promoStorageKey => {
    return {
      storedPromo: promoStorageKey
        ? getLocalStorage(promoStorageKey)
        : getLocalStorage('appliedPromoCode'),
      storedPrev: promoStorageKey
        ? getLocalStorage(`${promoStorageKey}_previous`)
        : getLocalStorage('appliedPromoPrevious'),
      storedCurrent: promoStorageKey
        ? getLocalStorage(`${promoStorageKey}_current`)
        : getLocalStorage('appliedPromoCurrent'),
    }
  }

  updateStoredPromoMessageState = (
    storedPrev,
    storedCurrent,
    promoStorageKey
  ) => {
    if (!storedPrev && !storedCurrent) {
      return
    }

    this.previousAppliedPromo = storedPrev || ''
    this.currentAppliedPromo = storedCurrent || ''

    if (this.previousAppliedPromo || this.currentAppliedPromo) {
      this.toShowAlertMsg = true
    }

    if (this.previousAppliedPromo === this.currentAppliedPromo) {
      this.clearStoredPromoReplacementValues(promoStorageKey)
    }
  }

  handleMultiPromoCleanup = async (promoStorageKey, requestHeaders) => {
    if (this.hasRunPromoCleanup) {
      return
    }

    this.hasRunPromoCleanup = true
    const deleteResult = await checkoutContainer.deletePromo()
    if (checkoutContainer.isSuccessResponse(deleteResult)) {
      this.clearStoredPromoValues(promoStorageKey)
      toastState?.setToastMessage(
        i18nTranslate(
          'response.deletePromocode',
          'Promo code removed successfully'
        ),
        true
      )
    }

    await cartContainer?.promoCodeList?.({
      ...requestHeaders,
      fetchAllPromoCodes: this.getFetchAllPromoCodesParam(),
    })
  }

  handleStoredPromoState = promoStorageKey => {
    const { storedPromo, storedPrev, storedCurrent } =
      this.readStoredPromoValues(promoStorageKey)
    this.updateStoredPromoMessageState(
      storedPrev,
      storedCurrent,
      promoStorageKey
    )
    return storedPromo
  }

  syncAppliedPromoToStorage = promoStorageKey => {
    const appliedCodes =
      cartContainer?.cartResponse?.promotionDetails?.codes || []
    const existingApplied =
      Array.isArray(appliedCodes) && appliedCodes.length > 0

    if (!existingApplied) {
      return false
    }

    const existingCode = appliedCodes?.[0]?.code
    if (!existingCode) {
      return false
    }

    if (promoStorageKey) {
      setLocalStorage(promoStorageKey, existingCode)
    } else {
      setLocalStorage('appliedPromoCode', existingCode)
    }

    return true
  }

  handleSinglePromoState = async promoStorageKey => {
    migrateOldPromoStorageKey(promoStorageKey)
    const storedPromo = this.handleStoredPromoState(promoStorageKey)
    const hasExistingAppliedPromo =
      this.syncAppliedPromoToStorage(promoStorageKey)

    if (!hasExistingAppliedPromo && storedPromo) {
      await this.handleApplyClick(storedPromo, 'manual', true)
    }
  }

  initializeDisablePromoStackingState = async requestHeaders => {
    const promoStorageKey = getScopedAppliedPromoKey(this.disablePromoStacking)
    const existingAppliedCodes =
      cartContainer?.cartResponse?.promotionDetails?.codes || []

    if (existingAppliedCodes.length > 1) {
      await this.handleMultiPromoCleanup(promoStorageKey, requestHeaders)
      return
    }

    await this.handleSinglePromoState(promoStorageKey)
  }

  trackInitialPromotionsIfNeeded = () => {
    if (
      !enableNewFeature('enableRedesignGTMPromocode') ||
      globalTrackingState.hasTrackedInitialPromotions
    ) {
      return
    }

    const existingProductPromotions = this.getExistingProductPromotions()
    if (existingProductPromotions.length > 0) {
      globalTrackingState.hasTrackedInitialPromotions = true
    }
  }

  async componentDidMount() {
    const isLoyaltyVoucherEnabled = isFeatureEnabled('globalLoyalty')
    const requestHeaders = this.getPromoListRequestHeaders()

    await this.initializeLoyaltyData(isLoyaltyVoucherEnabled)

    await cartContainer?.promoCodeList?.({
      ...requestHeaders,
      fetchAllPromoCodes: this.getFetchAllPromoCodesParam(),
    })
    checkoutContainer.isToDisableSubmitOrderButton = true
    !this.isFromSubscription &&
      (await this.handlePaymentOnPromoAddOrRemove({ action: 'init' }))

    if (this.disablePromoStacking && !this.isFromSubscription) {
      await this.initializeDisablePromoStackingState(requestHeaders)
    }

    this.trackInitialPromotionsIfNeeded()
  }

  getPromotionOfferType = promoCode => {
    const appliedCodes =
      cartContainer?.cartResponse?.promotionDetails?.codes || []
    const appliedPromoData = appliedCodes?.find?.(
      promo => promo?.code === promoCode
    )

    if (appliedPromoData?.code) {
      return appliedPromoData.code
    }

    const promoCodeList = cartContainer?.promoCodesList || []
    const promoData = promoCodeList?.find?.(promo => promo?.code === promoCode)

    if (promoData?.code) {
      return promoData.code
    }

    if (cartContainer?.hasSubscriptionProduct?.()) {
      return OFFER_TYPES.SUBSCRIPTION
    }

    const cartItems = cartContainer?.cartResponse?.items || []
    const hasBundleProducts = cartItems?.some(
      item =>
        item?.type === OFFER_TYPES.BUNDLE ||
        item?.skus?.some(sku => sku?.type === OFFER_TYPES.BUNDLE)
    )

    if (hasBundleProducts) {
      return OFFER_TYPES.PRICE_OFFER_BUNDLE
    }
    return OFFER_TYPES.PERSONAL_OFFER
  }

  getExistingProductPromotions = () => {
    const cartItems = this.isFromSubscription
      ? []
      : cartContainer?.cartResponse?.items || []
    const promotionDetails = this.isFromSubscription
      ? subscriptionContainer?.subscriptionPreferenceResponse?.promotionDetails
      : cartContainer?.cartResponse?.promotionDetails
    const appliedCodes = promotionDetails?.codes || []
    const appliedPromoCodes = appliedCodes?.map?.(code => code?.code)
    const existingPromotions = []

    cartItems?.forEach?.(item => {
      if (
        item?.promotionDetails?.promotions &&
        Array?.isArray?.(item?.promotionDetails?.promotions)
      ) {
        item?.promotionDetails?.promotions?.forEach?.(promotionItem => {
          if (
            promotionItem?.breakup &&
            Array?.isArray?.(promotionItem?.breakup)
          ) {
            promotionItem?.breakup?.forEach?.(promo => {
              const promoOffer =
                promo?.title || promo?.displayMessage || promo?.code
              if (promoOffer && !appliedPromoCodes?.includes?.(promo?.code)) {
                existingPromotions?.push?.({
                  offer: promoOffer,
                  method: 'automatic',
                })
              }
            })
          }
        })
      }

      if (
        item?.type === 'BUNDLE' ||
        item?.type === 'bundle' ||
        item?.skus?.some?.(
          sku => sku?.type === 'BUNDLE' || sku?.type === 'bundle'
        )
      ) {
        if (item?.promotionDetails?.promotions) {
          item?.promotionDetails?.promotions?.forEach?.(promotionItem => {
            if (
              promotionItem?.breakup &&
              Array?.isArray?.(promotionItem?.breakup)
            ) {
              promotionItem?.breakup?.forEach?.(promo => {
                if (
                  (promo?.title?.toLowerCase?.()?.includes?.('bundle') ||
                    promo?.displayMessage
                      ?.toLowerCase?.()
                      ?.includes?.('bundle')) &&
                  !appliedPromoCodes?.includes?.(promo?.code)
                ) {
                  existingPromotions?.push?.({
                    offer:
                      promo?.title ||
                      promo?.displayMessage ||
                      'Bundle discount',
                    method: 'automatic',
                  })
                }
              })
            }
          })
        }
      }

      item?.skus?.forEach?.(sku => {
        if (
          sku?.promotionDetails?.promotions &&
          Array?.isArray?.(sku?.promotionDetails?.promotions)
        ) {
          sku?.promotionDetails?.promotions?.forEach?.(promotionItem => {
            if (
              promotionItem?.breakup &&
              Array?.isArray?.(promotionItem?.breakup)
            ) {
              promotionItem?.breakup?.forEach?.(promo => {
                const promoOffer =
                  promo?.title || promo?.displayMessage || promo?.code
                if (promoOffer && !appliedPromoCodes?.includes?.(promo?.code)) {
                  existingPromotions?.push?.({
                    offer: promoOffer,
                    method: 'automatic',
                  })
                }
              })
            }
          })
        }
      })
    })

    const cartPromotions = promotionDetails?.promotions || []
    cartPromotions?.forEach?.(promotionItem => {
      if (promotionItem?.breakup && Array?.isArray?.(promotionItem?.breakup)) {
        promotionItem?.breakup?.forEach?.(promo => {
          const promoOffer =
            promo?.title || promo?.displayMessage || promo?.code
          if (promoOffer && !appliedPromoCodes?.includes?.(promo?.code)) {
            existingPromotions?.push?.({
              offer: promoOffer,
              method: 'automatic',
            })
          }
        })
      }
    })

    const uniquePromotions = existingPromotions?.filter?.(
      (promo, index, self) =>
        index === self?.findIndex?.(p => p?.offer === promo?.offer)
    )
    return uniquePromotions
  }

  getCreativeName = promoCode => {
    const promoCodeList = cartContainer?.promoCodesList || []
    const promoData = promoCodeList?.find?.(promo => promo?.code === promoCode)

    return (
      promoData?.creativeName ||
      promoData?.displayName ||
      promoData?.type ||
      'NA'
    )
  }

  getPromotionName = promoCode => {
    const promoCodeList = cartContainer?.promoCodesList || []
    const promoData = promoCodeList?.find?.(promo => promo?.code === promoCode)

    return promoData?.name || promoData?.description || promoData?.title || 'NA'
  }

  trackViewPromotionEvent = (forceTrack = false) => {
    const promoCodeList = cartContainer?.promoCodesList || []

    if (promoCodeList?.length > 0) {
      const visiblePromoCount = this.showMore
        ? promoCodeList?.length
        : this.showMoreDefaultLength
      const visiblePromos = promoCodeList?.slice?.(0, visiblePromoCount)

      const promotionsArray = visiblePromos
        ?.map?.(promo => {
          const creativeName = this.getCreativeName(promo?.code)
          const promotionName = this.getPromotionName(promo?.code)
          const creativeSlot =
            promo?.creativeSlot || promo?.slot || promo?.category || 'NA'
          const promotionId = promo?.code || ''

          if (promotionId) {
            return {
              creative_name: creativeName,
              creative_slot: creativeSlot,
              promotion_id: promotionId,
              promotion_name: promotionName,
            }
          }
          return null
        })
        ?.filter?.(Boolean)

      if (
        promotionsArray.length > 0 &&
        (forceTrack || !globalTrackingState?.hasTrackedInitialPromotions)
      ) {
        if (enableNewFeature('enableRedesignGTMPromocode')) {
          trackRedesignViewPromotion({
            promotions: promotionsArray,
          })
        }
        globalTrackingState.hasTrackedInitialPromotions = true
        globalTrackingState?.trackedPromotionIds?.clear?.()
        promotionsArray?.forEach?.(promo => {
          globalTrackingState?.trackedPromotionIds?.add?.(promo?.promotion_id)
        })
      }
    }
  }

  trackSelectPromotionEvent = promoCode => {
    const promoCodeList = cartContainer?.promoCodesList || []
    const selectedPromo = promoCodeList?.find?.(
      promo => promo?.code === promoCode
    )

    if (selectedPromo && promoCode) {
      const creativeName = this.getCreativeName(promoCode)
      const promotionName = this.getPromotionName(promoCode)
      const creativeSlot =
        selectedPromo?.creativeSlot ||
        selectedPromo?.slot ||
        selectedPromo?.category ||
        'NA'

      if (enableNewFeature('enableRedesignGTMPromocode')) {
        trackRedesignSelectPromotion({
          creative_name: creativeName,
          creative_slot: creativeSlot,
          promotion_id: promoCode,
          promotion_name: promotionName,
        })
      }
    }
  }

  get validSubscriptionPromoCodes() {
    return (
      subscriptionContainer?.subscriptionPreferenceResponse?.promotionDetails?.codes?.filter(
        code => code?.isValid === 'true' || code?.isValid === true
      ) || []
    )
  }

  refreshSubscriptionPromoData = async promoCode => {
    const subscriptionMonth =
      subscriptionContainer?.subscriptionPromoMonth || ''
    const subscriptionDate = subscriptionContainer?.subscriptionPromoDate || ''
    if (this.isFromSubscription) {
      const response =
        await subscriptionContainer?.getAllSubscriptionsPreference?.(
          customerContainer?.userId,
          subscriptionMonth,
          false,
          '',
          subscriptionDate
        )
      if (promoCode) {
        const isPromoCodeMatched =
          response?.promotionDetails?.codes?.filter(
            code => code?.code?.toLowerCase() === promoCode?.toLowerCase()
          )?.[0]?.isValid || false
        const isPromoCodeValid =
          isPromoCodeMatched === 'true' || isPromoCodeMatched === true
        return isPromoCodeValid ? { status: 'success' } : { status: 'failure' }
      }
      return response
    }
    return null
  }

  handleApplyCouponCode = async (
    formData,
    method = 'manual',
    isRestoringPromo = false,
    promoCodeToReplace = ''
  ) => {
    this.isCallInProgress = true
    let promoCode = formData?.promoCode?.trim?.() || ''
    const subscriptionMonth =
      subscriptionContainer?.subscriptionPromoMonth || ''
    let response
    if (this.isFromSubscription) {
      response = await subscriptionContainer?.updatePromo?.(
        subscriptionMonth,
        promoCode,
        'add',
        promoCodeToReplace
      )
    } else {
      response = await checkoutContainer?.addPromo?.(promoCode)
    }
    trackCheckoutCouponCodeEvents({
      coupon: promoCode,
    })
    if (
      this.isFromSubscription &&
      checkoutContainer?.isSuccessResponse?.(response)
    ) {
      subscriptionContainer.subscriptionPromoApplied = true
      response = await this.refreshSubscriptionPromoData(promoCode)
    }
    if (checkoutContainer?.isSuccessResponse?.(response)) {
      this.formikRef.current?.resetForm()
      if (!isRestoringPromo) {
        toastState?.setToastMessage(
          response?.message ||
            i18nTranslate(
              'response.SuccessPromocode',
              'Promo code applied successfully'
            ),
          true
        )
      }
      this.currentAppliedPromo = promoCode
      if (this.disablePromoStacking && !this.isFromSubscription && promoCode) {
        const promoStorageKey = getScopedAppliedPromoKey(
          this.disablePromoStacking
        )
        if (promoStorageKey) {
          setLocalStorage(promoStorageKey, promoCode)
        }
      }
      if (this.previousAppliedPromo) this.toShowAlertMsg = true
      if (enableNewFeature?.('enableRedesignGTMPromocode')) {
        const existingProductPromotions = this?.getExistingProductPromotions?.()

        const allOffers = []
        const allMethods = []

        allOffers?.push?.(promoCode)
        allMethods?.push?.(method)

        existingProductPromotions?.forEach?.(promo => {
          allOffers?.push?.(promo?.offer)
          allMethods?.push?.(promo?.method)
        })

        const uniqueOffers = []
        const uniqueMethods = []
        const seenOffers = new Set()

        for (let i = 0; i < allOffers?.length; i++) {
          if (!seenOffers?.has?.(allOffers?.[i])) {
            uniqueOffers?.push?.(allOffers?.[i])
            uniqueMethods?.push?.(allMethods?.[i])
            seenOffers?.add?.(allOffers?.[i])
          }
        }

        trackRedesignAddCoupon?.({
          offer: uniqueOffers?.length > 1 ? uniqueOffers : uniqueOffers?.[0],
          method:
            uniqueMethods?.length > 1 ? uniqueMethods : uniqueMethods?.[0],
        })
      }

      const fetchAllPromoCodesParam = showAllPromoCodesInCheckout()
        ? 'true'
        : 'false'

      await cartContainer?.promoCodeList?.({
        ...(this.isFromSubscription ? { 'x-summary-cart': true } : {}),
        fetchAllPromoCodes: fetchAllPromoCodesParam,
      })
      !this.isFromSubscription &&
        (await this.handlePaymentOnPromoAddOrRemove({ action: 'add' }))
      if (isExpressCheckout()) {
        await cartContainer?.viewCart?.()
      }
      this.isToResetForm = true
      this.isCallInProgress = false
      return response
    } else if (
      response?.code === 'EOCAPRC001' ||
      response?.status === 'failure'
    ) {
      if (!isRestoringPromo) {
        toastState?.setToastMessage(
          i18nTranslate(
            'response.invalidPromocode',
            'The promotion code entered is invalid.'
          ),
          false
        )
      }
      this.isCallInProgress = false
    } else {
      if (!isRestoringPromo) {
        toastState?.setToastMessage(
          response?.message ||
            i18nTranslate(
              'response.failurePromocode',
              'unable to add the promo code'
            ),
          false
        )
      }
      this.isCallInProgress = false
    }
    await cartContainer?.promoCodeList?.(
      this.isFromSubscription ? { 'x-summary-cart': true } : {}
    )
    globalTrackingState?.reset?.()
    this.appliedPromoPreview = null
    return response
  }

  handleApplyClick = async (
    formDataOrCode,
    method = 'manual',
    isRestoringPromo = false
  ) => {
    const promoCode =
      typeof formDataOrCode === 'string'
        ? formDataOrCode
        : formDataOrCode?.promoCode
    if (this.isReplacingPromo || this.isCallInProgress) return

    const appliedPromoCodeList = this.isFromSubscription
      ? this.validSubscriptionPromoCodes
      : cartContainer?.cartResponse?.promotionDetails?.codes || []
    const existingApplied =
      Array.isArray(appliedPromoCodeList) && appliedPromoCodeList.length > 0
    const existingCode = appliedPromoCodeList?.find?.(
      promo => promo?.code === promoCode
    )?.code

    if (existingCode === promoCode) {
      this.previousAppliedPromo = existingCode
      this.currentAppliedPromo = promoCode
      this.toShowAlertMsg = true
      if (!this.isFromSubscription) {
        const promoStorageKey = getScopedAppliedPromoKey(
          this.disablePromoStacking
        )
        if (promoStorageKey) {
          deleteFromLocalStorage(`${promoStorageKey}_previous`)
          deleteFromLocalStorage(`${promoStorageKey}_current`)
        } else {
          deleteFromLocalStorage('appliedPromoPrevious')
          deleteFromLocalStorage('appliedPromoCurrent')
        }
      }
      return
    }

    if (this.disablePromoStacking && existingApplied) {
      // Use service to determine replacement strategy
      const strategy = this.promoStackingService.determineReplacementStrategy(
        promoCode,
        appliedPromoCodeList
      )

      if (strategy.canApplyDirectly) {
        this.toShowAlertMsg = false
        return this.handleApplyCouponCode(
          { promoCode },
          method,
          isRestoringPromo
        )
      }

      const promoCodeToReplace = strategy.promoToReplace

      if (!promoCodeToReplace) {
        return this.handleApplyCouponCode({ promoCode }, method)
      }

      try {
        this.isReplacingPromo = true
        const promoStorageKey = getScopedAppliedPromoKey(
          this.disablePromoStacking
        )
        const deletePromoResponse = !this.isFromSubscription
          ? await checkoutContainer?.deletePromo?.(promoCodeToReplace)
          : { status: 'success' } // Assume success for subscription promo removal since API doesn't return response
        if (checkoutContainer?.isSuccessResponse?.(deletePromoResponse)) {
          this.previousAppliedPromo = promoCodeToReplace
          const addPromoResponse = await this.handleApplyCouponCode(
            { promoCode },
            method,
            isRestoringPromo,
            promoCodeToReplace
          )
          if (checkoutContainer?.isSuccessResponse?.(addPromoResponse)) {
            this.appliedPromoPreview = promoCode
          }
          if (!checkoutContainer?.isSuccessResponse?.(addPromoResponse)) {
            await this.handleApplyCouponCode(
              { promoCode: promoCodeToReplace },
              'manual',
              isRestoringPromo
            )
            this.toShowAlertMsg = false
            if (!this.isFromSubscription) {
              if (promoStorageKey) {
                deleteFromLocalStorage(`${promoStorageKey}_previous`)
                deleteFromLocalStorage(`${promoStorageKey}_current`)
              } else {
                deleteFromLocalStorage('appliedPromoPrevious')
                deleteFromLocalStorage('appliedPromoCurrent')
              }
            }
          } else {
            this.currentAppliedPromo = promoCode
            this.toShowAlertMsg = true
            if (!this.isFromSubscription) {
              if (promoStorageKey) {
                setLocalStorage(
                  `${promoStorageKey}_previous`,
                  promoCodeToReplace
                )
                setLocalStorage(`${promoStorageKey}_current`, promoCode)
              } else {
                setLocalStorage('appliedPromoPrevious', promoCodeToReplace)
                setLocalStorage('appliedPromoCurrent', promoCode)
              }
            }
          }
        } else {
          toastState?.setToastMessage(
            deletePromoResponse?.message ||
              i18nTranslate(
                'response.failureDeletePromo',
                'unable remove the promo code'
              ),
            false
          )
        }
      } catch (e) {
        toastState?.setToastMessage(
          i18nTranslate(
            'response.failurePromocode',
            'unable to add the promo code'
          ),
          false
        )
      } finally {
        this.isReplacingPromo = false
        await cartContainer?.promoCodeList?.(
          this.isFromSubscription ? { 'x-summary-cart': true } : {}
        )
        this.appliedPromoPreview = null
      }
    } else {
      this.toShowAlertMsg = false
      return this.handleApplyCouponCode(
        typeof formDataOrCode === 'string'
          ? { promoCode: formDataOrCode }
          : formDataOrCode,
        method,
        isRestoringPromo
      )
    }
  }

  handleDeleteCouponCode = async promoCode => {
    const subscriptionMonth =
      subscriptionContainer?.subscriptionPromoMonth || ''
    let response
    if (this.isFromSubscription) {
      response = await subscriptionContainer?.updatePromo?.(
        subscriptionMonth,
        promoCode,
        'remove'
      )
    } else {
      response = await checkoutContainer?.deletePromo?.(promoCode)
    }
    if (checkoutContainer?.isSuccessResponse?.(response)) {
      toastState?.setToastMessage(
        response?.message ||
          i18nTranslate(
            'response.deletePromocode',
            'Promo code removed successfully'
          ),
        true
      )
      await this.refreshSubscriptionPromoData()
      !this.isFromSubscription &&
        (await this.handlePaymentOnPromoAddOrRemove({ action: 'remove' }))
      if (this.disablePromoStacking && !this.isFromSubscription) {
        const promoStorageKey = getScopedAppliedPromoKey(
          this.disablePromoStacking
        )
        if (promoStorageKey) {
          deleteFromLocalStorage(promoStorageKey)
          deleteFromLocalStorage(`${promoStorageKey}_previous`)
          deleteFromLocalStorage(`${promoStorageKey}_current`)
        } else {
          deleteFromLocalStorage('appliedPromoCode')
          deleteFromLocalStorage('appliedPromoPrevious')
          deleteFromLocalStorage('appliedPromoCurrent')
        }
      }
      this.previousAppliedPromo = ''
      this.currentAppliedPromo = ''
      this.toShowAlertMsg = false
      this.appliedPromoPreview = null
    } else {
      toastState?.setToastMessage(
        response?.message ||
          i18nTranslate(
            'response.failureDeletePromo',
            'unable remove the promo code'
          ),
        false
      )
    }
    if (response?.status === 'failure') {
      toastState?.setToastMessage(
        i18nTranslate(
          'response.notExistedPromo',
          'The promotion codes requested do not exist in the cart.'
        ),
        false
      )
    }

    await cartContainer?.promoCodeList?.({
      ...(this.isFromSubscription ? { 'x-summary-cart': true } : {}),
      fetchAllPromoCodes: this.getFetchAllPromoCodesParam(),
    })
    globalTrackingState?.reset?.()
  }

  renderSubmitButton = () => {
    return (
      <Button
        data-testid="qa-pay-promo-apply"
        className={`rounded-0 promo-apply-button promo-code w-100 text-capitalize btn-bg-blue promo-code-btn`}
        variant="primary"
        disabled={this.disablePromotion()}
        type="submit">
        {i18nTranslate('couponCode.submit', 'apply')}
      </Button>
    )
  }

  renderPromos = promotionDetails => {
    return (
      <Button
        className="promo-code mr-1 mb-1"
        variant="outline-dark"
        data-testid="qa-promo-remove"
        onClick={() => this.handleDeleteCouponCode(promotionDetails?.code)}>
        <span> {promotionDetails?.code}</span>
        <CloseIcon size="2rem" className="promo-close-icon" />
      </Button>
    )
  }

  handlePaymentOnPromoAddOrRemove = async (options = {}) => {
    const { action = '' } = options
    const {
      isStoreCredit = false,
      onContinue = function () {},
      updateFlowPaths = function () {},
    } = this.props

    const { payments = [], value = {} } =
      (await cartContainer?.cartResponse) || {}
    let grandTotal = value?.grandTotal || 0

    let activePath = checkoutContainer?.activePath

    let isFromVenmoPayment = getLocalStorage?.('isFromVenmoPayment') || false
    const isVenmoPayment =
      payments?.some?.(payment => {
        const type = payment?.type?.toLowerCase?.()
        return type === 'venmo'
      }) || false
    const isNotFromMobileRedirectVenmoPayment = !(
      isFromVenmoPayment && isVenmoPayment
    )

    let paymentDeleted = false
    if (Array?.isArray?.(payments) && payments?.length > 0) {
      if (isNotFromMobileRedirectVenmoPayment) {
        await checkoutContainer?.deletePayment?.()
      }
      paymentDeleted = true
    }
    let isCartHavingSubscriptionProduct =
      cartContainer?.hasSubscriptionProduct?.()

    let paymentOption = isStoreCredit ? 'storecredit' : 'payment'
    if (action === 'add' || action === 'remove') {
      if (grandTotal > 0) {
        updateFlowPaths?.()
        if (
          activePath === 'submitOrder' ||
          activePath === 6 ||
          (activePath === 'subscription' && isCartHavingSubscriptionProduct) ||
          (activePath === '' && paymentDeleted)
        ) {
          checkoutContainer.activePath = paymentOption
        }
      } else if (grandTotal === 0) {
        updateFlowPaths?.()
        if (
          isCartHavingSubscriptionProduct &&
          (activePath === 'payment' || activePath === 'subscription')
        ) {
          checkoutContainer.activePath = 'payment'
        } else if (activePath === 'payment' || activePath === 'storecredit') {
          checkoutContainer.activePath = 'shippingMethods'
        } else if (
          activePath === 'submitOrder' ||
          activePath === 6 ||
          (activePath === '' && paymentDeleted)
        ) {
          checkoutContainer.activePath = 'shippingMethods'
          onContinue?.()
        }
      }
    }
  }

  disablePromotion = () => {
    const { math = {} } = this.props
    let grandTotal = math?.['grandTotal'] || 0
    if (this.isCallInProgress) {
      return true
    }
    if (this.isReplacingPromo) {
      return true
    }
    if (grandTotal > 0) {
      return false
    } else {
      return true
    }
  }

  handleShowMore = () => {
    const isExpanding = this.showMore

    if (!isExpanding) {
      const section = document.querySelector('.promo-accordion')
      section?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    this.showMore = !this.showMore
  }
  getVouchers = () => {
    const resp = loyaltyContainer?.loyaltyByIdResponse
    const userMath = resp?.userMath || resp?.user_math
    const vouchers = userMath?.VOUCHER?.vouchers || []
    return vouchers
  }

  isVoucherApplied = voucherType => {
    return !!this.voucherAppliedMap[voucherType]
  }

  applyVoucherToCart = async voucher => {
    const totalPointsToPay =
      Number(
        cartContainer?.cartResponse?.loyalty?.userMath?.VOUCHER
          ?.totalPointsToPay ?? 0
      ) || 0

    if (totalPointsToPay === 0) {
      toastState?.setToastMessage(
        i18nTranslate(
          'voucher.voucherCannotBeApplied',
          'Voucher cannot be applied.'
        ),
        false
      )
      return
    }

    this.isCallInProgress = true
    const voucherAmount =
      Number((voucher?.voucherType || '').replace('VOUCHER_', '')) || 0
    const response = await cartContainer.updateCartByType([
      {
        value: voucherAmount,
        points: voucherAmount,
        type: 'VOUCHER',
        valueType: 'VOUCHER',
        voucherType: voucher?.voucherType,
        voucherId: voucher?.identifier,
      },
    ])
    if (cartContainer?.isSuccessResponse?.(response)) {
      toastState?.setToastMessage(
        response?.message ||
          i18nTranslate(
            'response.SuccessLoyaltyVoucher',
            'Loyalty voucher applied successfully'
          ),
        true
      )

      runInAction(() => {
        this.voucherAppliedMap = {
          ...this.voucherAppliedMap,
          [voucher?.voucherType]: true,
        }
      })
    } else {
      toastState?.setToastMessage(
        response?.message ||
          i18nTranslate(
            'response.failureLoyaltyVoucher',
            'Unable to apply loyalty voucher'
          ),
        false
      )
    }
    this.isCallInProgress = false
    return response
  }

  handleVoucherApply = async voucher => {
    const isVoucherEligibleToRedeem = convertToBoolean(
      cartContainer?.cartResponse?.properties?.isVoucherEligibleToRedeem ??
        false
    )
    if (!isVoucherEligibleToRedeem) {
      return
    }

    const totalPointsToPay =
      Number(
        cartContainer?.cartResponse?.loyalty?.userMath?.VOUCHER
          ?.totalPointsToPay ?? 0
      ) || 0

    if (totalPointsToPay === 0) {
      toastState?.setToastMessage(
        i18nTranslate(
          'response.voucherCannotBeApplied',
          "Your cart doesn't contain any voucher-redeemable products."
        ),
        false
      )
      this.isApplyVoucherDisabled = true
      return
    }

    const voucherAmount =
      Number((voucher?.voucherType || '').replace('VOUCHER_', '')) || 0
    const orderTotal =
      Number(
        cartContainer?.cartResponse?.value?.grandTotal ??
          this.props?.math?.grandTotal ??
          0
      ) || 0

    if (voucherAmount > orderTotal) {
      this.pendingVoucher = voucher
      this.isVoucherConfirmModalOpen = true
      return
    }

    await this.applyVoucherToCart(voucher)
  }

  handleConfirmVoucherApply = async () => {
    if (!this.pendingVoucher) {
      this.isVoucherConfirmModalOpen = false
      return
    }

    const response = await this.applyVoucherToCart(this.pendingVoucher)
    if (cartContainer?.isSuccessResponse?.(response)) {
      this.isVoucherConfirmModalOpen = false
      this.pendingVoucher = null
    }
  }

  handleCancelVoucherApply = () => {
    this.isVoucherConfirmModalOpen = false
    this.pendingVoucher = null
  }

  handleVoucherRemove = async voucher => {
    this.isCallInProgress = true
    const loyaltyDetails = cartContainer?.cartResponse?.loyaltyDetails || []
    const matched = loyaltyDetails.find(
      item => item?.voucherType === voucher?.voucherType
    )
    const loyaltyId = matched?.id
    const response = await cartContainer.removeVoucherFromCart(loyaltyId)
    if (response) {
      toastState?.setToastMessage(
        response?.message ||
          i18nTranslate(
            'response.deleteLoyaltyVoucher',
            'Loyalty voucher removed successfully'
          ),
        true
      )
      runInAction(() => {
        const updated = { ...this.voucherAppliedMap }
        delete updated[voucher?.voucherType]
        this.voucherAppliedMap = updated
      })
    }
    this.isCallInProgress = false
  }

  renderVoucherList = () => {
    const vouchers = this.getVouchers()
    if (!vouchers?.length) return null

    // Voucher apply is disabled when cart marks it ineligible for redemption
    const isVoucherEligibleToRedeem = convertToBoolean(
      cartContainer?.cartResponse?.properties?.isVoucherEligibleToRedeem ??
        false
    )

    // Get applied vouchers from cart
    const appliedVouchers = (
      cartContainer?.cartResponse?.loyaltyDetails || []
    ).filter(item => item?.type === 'VOUCHER')

    // Helper to render a voucher row
    const renderVoucherRow = (voucher, isApplied, key) => {
      let {
        date = '',
        month = '',
        year = '',
      } = dateFormatDeps?.convertTimeStampToDate?.(
        voucher?.expiryTime,
        false
      ) || {}
      return (
        <div
          key={key}
          className={`promo-code-list-container${isApplied ? ' applied-voucher' : ''}`}
          data-testid="qa-voucher-list-container">
          <div tabIndex={0}>
            <div className="promo-code-name" data-testid="qa-voucher-name">
              {voucher?.voucherType}
            </div>
            {(date || month || year) && (
              <div
                className="promo-code-exp-date"
                data-testid="qa-voucher-exp-date">
                {i18nTranslate('voucher.expire', 'Expire')}:{' '}
                {month + '/' + date + '/' + year}
              </div>
            )}
          </div>
          <div>
            {isApplied ? (
              <Button
                data-testid="qa-pay-voucher-remove"
                className="promo-apply-btn text-capitalize"
                variant="outline-danger"
                disabled={this.isCallInProgress}
                onClick={() => this.handleVoucherRemove(voucher)}>
                {i18nTranslate('voucher.Remove', 'Remove')}
              </Button>
            ) : (
              <Button
                data-testid="qa-pay-voucher-apply"
                className="promo-apply-btn text-capitalize"
                variant="primary"
                disabled={
                  this.isCallInProgress ||
                  this.isApplyVoucherDisabled ||
                  !isVoucherEligibleToRedeem
                }
                onClick={() => this.handleVoucherApply(voucher)}>
                {i18nTranslate('voucher.Apply', 'Apply')}
              </Button>
            )}
          </div>
        </div>
      )
    }

    // Render applied vouchers first (from cart), then available vouchers not yet applied
    const appliedVoucherTypes = new Set(appliedVouchers.map(v => v.voucherType))
    const appliedVoucherRows = appliedVouchers.map((applied, idx) => {
      // Find the voucher object for expiry info
      const voucherObj =
        vouchers.find(v => v.voucherType === applied.voucherType) || applied
      return renderVoucherRow(
        voucherObj,
        true,
        `applied-${applied.voucherType}`
      )
    })
    const availableVoucherRows = vouchers
      .filter(v => !appliedVoucherTypes.has(v.voucherType))
      .map((voucher, idx) =>
        renderVoucherRow(voucher, false, `available-${voucher.voucherType}`)
      )

    return <>{[...appliedVoucherRows, ...availableVoucherRows]}</>
  }

  handeleCustomBlur = fieldData => {
    const { values = {} } = fieldData || {}
    const promoCode = values?.promoCode || ''
    if (values && promoCode?.length >= 1) {
      this.isToResetForm = true
    } else {
      this.isToResetForm = false
    }
  }
  renderPromoCodeList = () => {
    const backendAppliedPromoCodeList = this.isFromSubscription
      ? this.validSubscriptionPromoCodes
      : cartContainer?.cartResponse?.promotionDetails?.codes || []
    let appliedPromoCodeList = backendAppliedPromoCodeList
    if (this.appliedPromoPreview && !this.isFromSubscription) {
      if (this.disablePromoStacking) {
        appliedPromoCodeList = [{ code: this.appliedPromoPreview }]
      } else {
        const isAlreadyInList = backendAppliedPromoCodeList.some(
          p => p.code === this.appliedPromoPreview
        )
        if (!isAlreadyInList) {
          appliedPromoCodeList = [
            ...backendAppliedPromoCodeList,
            { code: this.appliedPromoPreview },
          ]
        }
      }
    }
    const allPromoCodeList = cartContainer?.promoCodesList || []
    const formInputJson = formInput()
    const shouldShowAllPromos = showAllPromoCodesInCheckout()

    // Show all promos - flag for future use when backend provides eligibility data
    const promoCodeList = allPromoCodeList

    const maxLengthWhenShowMore = promoCodeList?.length
    const maxLengthDefault = this.showMoreDefaultLength
    const promoListMaxLength = this.showMore
      ? maxLengthWhenShowMore
      : maxLengthDefault
    const displayAppliedPromoCodes = Array.isArray(appliedPromoCodeList)
      ? appliedPromoCodeList
      : []
    const backendAppliedPromoCodes = Array.isArray(backendAppliedPromoCodeList)
      ? backendAppliedPromoCodeList
      : []
    const anyApplied = displayAppliedPromoCodes?.length > 0
    const isLoyaltyVoucherEnabled = isFeatureEnabled('globalLoyalty')

    return (
      <>
        <div className="promowallet-code-wrapper">
          <div
            data-testid="promocode-form-wrapper"
            className="promowallet-form-wrapper">
            <CommonForm
              schema={schema}
              formTypeJson={formInputJson}
              SubmitButtonProps={this.renderSubmitButton}
              onSubmit={this.handleApplyClick}
              hasInlineSubmitButton={true}
              customHandleBlur={this.handeleCustomBlur}
              isToResetForm={this.isToResetForm}
              enableReinitialize={this.isToResetForm}
              reference={this.formikRef}
            />
            {appliedPromoCodeList && (
              <div
                className="promowallet-code-list"
                data-testid="qa-applied-promocode">
                {appliedPromoCodeList?.map?.(this.renderPromos)}
              </div>
            )}
            {this.disablePromoStacking && anyApplied && (
              <div
                className="single-promo-message"
                data-testid="qa-single-promo-msg">
                {this.toShowAlertMsg ? (
                  this.previousAppliedPromo === this.currentAppliedPromo ? (
                    i18nTranslate(
                      'promocode.onlyOne',
                      'Only one promo code can be applied at a time.'
                    )
                  ) : (
                    <>
                      {i18nTranslate(
                        'promocode.replacedPrefix',
                        'Only one promo code can be applied at a time. Removed'
                      )}{' '}
                      <span className="promo-code-inline">
                        {this.previousAppliedPromo}
                      </span>{' '}
                      {i18nTranslate('promocode.replacedInfix', 'and applied')}{' '}
                      <span className="promo-code-inline">
                        {this.currentAppliedPromo}
                      </span>
                    </>
                  )
                ) : (
                  i18nTranslate(
                    'promocode.onlyOne',
                    'Only one promo code can be applied at a time. Please remove the existing promo code to add a new one.'
                  )
                )}
              </div>
            )}
          </div>
          {(promoCodeList?.length > 0 || this.getVouchers()?.length > 0) && (
            <div className="promo-code-list-wrapper">
              <div className="my-promo-title" tabIndex={0}>
                {i18nTranslate('promocode.listHeading', 'My Promos')}
              </div>
              {promoCodeList?.length > 0 &&
                promoCodeList
                  ?.slice?.(0, promoListMaxLength)
                  ?.map?.((promoCodeData, index) => {
                    const isApplied =
                      backendAppliedPromoCodes?.length > 0 &&
                      backendAppliedPromoCodes.find(
                        promo => promo?.code === promoCodeData?.code
                      )

                    let {
                      date = '',
                      month = '',
                      year = '',
                    } = dateFormatDeps?.convertTimeStampToDate?.(
                      promoCodeData?.expiryDate,
                      false
                    ) || {}
                    return (
                      <div key={index} className="promo-code-list-container">
                        <div tabIndex={0}>
                          <div
                            className="promo-code-name"
                            data-testid="qa-promo-code-name">
                            {promoCodeData?.code}
                          </div>
                          <div
                            className="promo-code-exp-date"
                            data-testid="qa-promo-code-exp-date">
                            {i18nTranslate('promoCode.expire', 'Expire')}:{' '}
                            {month + '/' + date + '/' + year}
                          </div>
                        </div>
                        <div>
                          <Button
                            data-testid="qa-pay-promocode-apply"
                            className={`promo-apply-btn text-capitalize ${isApplied?.code ? 'promo-apply-btn-disabled' : ''}`}
                            variant="primary"
                            disabled={
                              isApplied?.code ||
                              this.isReplacingPromo ||
                              this.isCallInProgress ||
                              (shouldShowAllPromos &&
                                promoCodeData?.isEligibleOffer === false)
                            }
                            onClick={() => {
                              // this.trackSelectPromotionEvent(promoCodeData?.code)
                              this.handleApplyClick(
                                promoCodeData?.code,
                                'manual'
                              )
                            }}>
                            {isApplied?.code
                              ? i18nTranslate('promocode.Applied', 'Applied')
                              : i18nTranslate('promocode.Apply', 'Apply')}
                          </Button>
                        </div>
                      </div>
                    )
                  })}
              {isLoyaltyVoucherEnabled && this.renderVoucherList()}
              {promoCodeList?.length > this.showMoreDefaultLength && (
                <div className="show-more-container">
                  {' '}
                  <Button
                    data-testid="qa-pay-promo-showmore"
                    className={`show-more-btn`}
                    variant="primary"
                    onClick={this.handleShowMore}>
                    {' '}
                    {this.showMore
                      ? i18nTranslate('promocode.seeLess', 'See less')
                      : i18nTranslate('promocode.seeMore', 'See more')}{' '}
                    {this.showMore ? (
                      <UpwardArrowIcon aria-hidden={true} />
                    ) : (
                      <DownwardArrowIcon aria-hidden={true} />
                    )}{' '}
                  </Button>{' '}
                </div>
              )}
            </div>
          )}
        </div>
        <Modal
          data-testid="qa-voucher-lower-voucherValue-confirm-modal"
          show={this.isVoucherConfirmModalOpen}
          onHide={this.handleCancelVoucherApply}
          className="voucher-confirm-modal"
          centered>
          <Modal.Header closeButton className="voucher-confirm-modal-header">
            <Modal.Title className="voucher-confirm-modal-title">
              <div className="icon-circle">
                <FaExclamationTriangle size={22} />
              </div>
              {i18nTranslate('voucher.confirmPopupTitle', 'Are you sure?')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="voucher-confirm-modal-body">
            <div className="voucher-confirm-message">
              {i18nTranslate(
                'voucher.voucherHigherThanTotalMessage',
                'Looks like your voucher is higher than your order total!'
              )}
            </div>
            <div className="voucher-confirm-submessage">
              {i18nTranslate(
                'voucher.remainingBalanceForfeitedMessage',
                'The remaining balance will be forfeited.'
              )}
            </div>
            <Button
              variant="outline-dark"
              className="voucher-confirm-apply-btn"
              onClick={this.handleConfirmVoucherApply}>
              {i18nTranslate(
                'voucher.applyPromoConfirmButton',
                'Yes, apply promo'
              )}
            </Button>
            <Button
              variant="link"
              className="voucher-confirm-nevermind-btn"
              onClick={this.handleCancelVoucherApply}>
              {i18nTranslate('voucher.neverMindButton', 'Nevermind')}
            </Button>
          </Modal.Body>
        </Modal>
      </>
    )
  }

  render() {
    return <>{this.renderPromoCodeList()}</>
  }
}

PromoWalletCheckout.propTypes = {
  math: PropTypes.shape({
    grandTotal: PropTypes.number,
  }).isRequired,
  isFromSubscription: PropTypes.bool,
  updateFlowPaths: PropTypes.func,
  onContinue: PropTypes.func,
}

export { PromoWalletCheckout }
export default PromoWalletCheckout
